import { QueryClientProvider } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { t } from 'i18next';
import { AddressBadge, Button, ButtonStyle, Dialog, DialogSize, Heading, Tabs, VerticalTabs, } from '@sovryn/ui';
import { RSK_CHAIN_ID } from '../../../config/chains';
import { MobileCloseButton } from '../../1_atoms/MobileCloseButton/MobileCloseButton';
import { useAccount } from '../../../hooks/useAccount';
import { useChainStore } from '../../../hooks/useChainStore';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { translations } from '../../../locales/i18n';
import { ACTIVE_CLASSNAME, queryClient } from './ERC20BridgeDialog.constants';
import { ReceiveFlow } from './components/ReceiveFlow/ReceiveFlow';
import { SendFlow } from './components/SendFlow/SendFlow';
import { ReceiveFlowContextProvider } from './contextproviders/ReceiveFlowContext';
import { SendFlowContextProvider } from './contextproviders/SendFlowContext';
const translation = translations.erc20Bridge.mainScreen;
export const ERC20BridgeDialog = ({ isOpen, onClose, step = 0, }) => {
    const [index, setIndex] = useState(step);
    const { account } = useAccount();
    const { isMobile } = useIsMobile();
    const { currentChainId, setCurrentChainId } = useChainStore();
    const isWrongChain = useMemo(() => RSK_CHAIN_ID !== currentChainId, [currentChainId]);
    const handleClose = useCallback(() => {
        if (isWrongChain) {
            setCurrentChainId(RSK_CHAIN_ID);
        }
        onClose();
    }, [onClose, isWrongChain, setCurrentChainId]);
    useEffect(() => {
        setIndex(step);
    }, [step]);
    const items = useMemo(() => {
        return [
            {
                label: t(translation.tabs.receiveLabel),
                infoText: t(translation.tabs.receiveInfoText),
                content: (React.createElement(ReceiveFlowContextProvider, null,
                    React.createElement(ReceiveFlow, { onClose: handleClose }),
                    React.createElement(MobileCloseButton, { onClick: handleClose }))),
                activeClassName: ACTIVE_CLASSNAME,
                dataAttribute: 'erc20-bridge-receive',
            },
            {
                label: t(translation.tabs.sendLabel),
                infoText: t(translation.tabs.sendInfoText),
                content: (React.createElement(SendFlowContextProvider, null,
                    React.createElement(SendFlow, { onClose: handleClose }),
                    React.createElement(MobileCloseButton, { onClick: handleClose }))),
                activeClassName: ACTIVE_CLASSNAME,
                dataAttribute: 'erc20-bridge-send',
            },
        ];
    }, [handleClose]);
    const onChangeIndex = useCallback((index) => {
        index !== null ? setIndex(index) : setIndex(0);
    }, []);
    const dialogSize = useMemo(() => (isMobile ? DialogSize.md : DialogSize.xl3), [isMobile]);
    useEffect(() => {
        setIndex(0);
    }, [account]);
    return (React.createElement(Dialog, { isOpen: isOpen, width: dialogSize, className: "p-4 flex items-center sm:p-0", disableFocusTrap: true, closeOnEscape: false },
        React.createElement(QueryClientProvider, { client: queryClient },
            React.createElement(Tabs, { index: index, items: items, onChange: onChangeIndex, className: "w-full md:hidden", contentClassName: "pt-9 px-6 pb-7 h-full" }),
            React.createElement(VerticalTabs, { items: items, onChange: onChangeIndex, selectedIndex: index, tabsClassName: "min-h-[42rem] h-auto self-stretch block pt-0 relative flex-1", headerClassName: "pb-0 pt-5", footerClassName: "absolute bottom-5 left-5", contentClassName: "px-9 pb-10 pt-6 flex-1", className: "hidden md:flex", header: () => (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "rounded bg-gray-60 px-2 py-1 w-fit mb-9" },
                        React.createElement(AddressBadge, { address: account })),
                    React.createElement(Heading, { className: "mb-20" }, t(translation.title)))), footer: () => (React.createElement(Button, { text: t(translations.common.buttons.close), onClick: handleClose, style: ButtonStyle.ghost, dataAttribute: "erc20-bridge-close" })) }))));
};
